/**
 * Input Validation Utility for XSS Protection
 *
 * This module provides validation and sanitization functions to prevent
 * Cross-Site Scripting (XSS) attacks and ensure safe input handling.
 */

// Regex pattern to detect potentially dangerous characters and HTML/script tags
const DANGEROUS_PATTERNS = {
  htmlTags: /<[^>]*>/g,
  scriptTag: /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
  eventHandlers: /on\w+\s*=/gi,
  javascript: /javascript\s*:/gi,
  dangerousChars: /[<>"'`]/g,
};

// Characters that should be encoded for safe display
const HTML_ENTITIES = {
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '`': '&#x60;',
  '/': '&#x2F;',
};

/**
 * Check if input contains potentially dangerous HTML/script content
 * @param {string} input - The input string to validate
 * @returns {boolean} - True if dangerous content is detected
 */
export const containsDangerousContent = (input) => {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const lowerInput = input.toLowerCase();

  // Check for script tags
  if (DANGEROUS_PATTERNS.scriptTag.test(input)) {
    return true;
  }

  // Check for any HTML tags
  if (DANGEROUS_PATTERNS.htmlTags.test(input)) {
    return true;
  }

  // Check for event handlers (onclick, onerror, etc.)
  if (DANGEROUS_PATTERNS.eventHandlers.test(input)) {
    return true;
  }

  // Check for javascript: protocol
  if (DANGEROUS_PATTERNS.javascript.test(input)) {
    return true;
  }

  // Check for dangerous characters
  if (DANGEROUS_PATTERNS.dangerousChars.test(input)) {
    return true;
  }

  return false;
};

/**
 * Validate a name field (First Name, Last Name)
 * Allows only alphanumeric characters, spaces, hyphens, apostrophes, and periods
 * @param {string} name - The name to validate
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export const validateName = (name) => {
  if (!name || typeof name !== 'string') {
    return { isValid: false, error: 'Name is required' };
  }

  const trimmedName = name.trim();

  if (trimmedName.length === 0) {
    return { isValid: false, error: 'Name cannot be empty' };
  }

  if (trimmedName.length > 100) {
    return { isValid: false, error: 'Name cannot exceed 100 characters' };
  }

  // Check for dangerous content first
  if (containsDangerousContent(name)) {
    return {
      isValid: false,
      error: 'Name contains invalid characters. Only letters, numbers, spaces, hyphens, apostrophes, and periods are allowed.'
    };
  }

  // Allow only safe characters: letters (including unicode), numbers, spaces, hyphens, apostrophes, periods
  const safeNamePattern = /^[\p{L}\p{N}\s\-'.]+$/u;

  if (!safeNamePattern.test(trimmedName)) {
    return {
      isValid: false,
      error: 'Name contains invalid characters. Only letters, numbers, spaces, hyphens, apostrophes, and periods are allowed.'
    };
  }

  return { isValid: true, error: null };
};

/**
 * Validate generic text input
 * @param {string} input - The input to validate
 * @param {object} options - Validation options
 * @param {number} options.maxLength - Maximum allowed length (default: 500)
 * @param {boolean} options.required - Whether the field is required (default: false)
 * @param {string} options.fieldName - Name of the field for error messages (default: 'Field')
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export const validateTextInput = (input, options = {}) => {
  const { maxLength = 500, required = false, fieldName = 'Field' } = options;

  if (!input || typeof input !== 'string') {
    if (required) {
      return { isValid: false, error: `${fieldName} is required` };
    }
    return { isValid: true, error: null };
  }

  const trimmedInput = input.trim();

  if (required && trimmedInput.length === 0) {
    return { isValid: false, error: `${fieldName} cannot be empty` };
  }

  if (trimmedInput.length > maxLength) {
    return { isValid: false, error: `${fieldName} cannot exceed ${maxLength} characters` };
  }

  if (containsDangerousContent(input)) {
    return {
      isValid: false,
      error: `${fieldName} contains invalid characters. HTML tags and special characters (<, >, ", ', \`) are not allowed.`
    };
  }

  return { isValid: true, error: null };
};

/**
 * Sanitize input by encoding HTML special characters
 * Use this for output encoding when displaying user-supplied content
 * @param {string} input - The input string to sanitize
 * @returns {string} - Sanitized string with HTML entities encoded
 */
export const sanitizeForDisplay = (input) => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  return input.replace(/[<>"'`/]/g, (char) => HTML_ENTITIES[char] || char);
};

/**
 * Strip all HTML tags from input
 * @param {string} input - The input string
 * @returns {string} - String with all HTML tags removed
 */
export const stripHtmlTags = (input) => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  return input.replace(DANGEROUS_PATTERNS.htmlTags, '');
};

/**
 * Create Ant Design form validation rule for name fields
 * @param {string} fieldName - Display name for error messages
 * @returns {function} - Ant Design validator function
 */
export const createNameValidator = (fieldName = 'Name') => {
  return (_, value) => {
    const result = validateName(value);
    if (result.isValid) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(result.error));
  };
};

/**
 * Create Ant Design form validation rule for generic text fields
 * @param {object} options - Validation options
 * @returns {function} - Ant Design validator function
 */
export const createTextValidator = (options = {}) => {
  return (_, value) => {
    const result = validateTextInput(value, options);
    if (result.isValid) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(result.error));
  };
};

/**
 * Get validation rules for Ant Design Form.Item for name fields
 * @param {string} fieldName - Display name for error messages
 * @param {boolean} required - Whether the field is required
 * @returns {array} - Array of Ant Design validation rules
 */
export const getNameValidationRules = (fieldName = 'Name', required = true) => {
  const rules = [];

  if (required) {
    rules.push({ required: true, message: `${fieldName} is required` });
  }

  rules.push({
    validator: createNameValidator(fieldName)
  });

  return rules;
};

/**
 * Get validation rules for Ant Design Form.Item for text fields
 * @param {object} options - Validation options
 * @returns {array} - Array of Ant Design validation rules
 */
export const getTextValidationRules = (options = {}) => {
  const { required = false, fieldName = 'Field', maxLength = 500 } = options;
  const rules = [];

  if (required) {
    rules.push({ required: true, message: `${fieldName} is required` });
  }

  if (maxLength) {
    rules.push({ max: maxLength, message: `${fieldName} cannot exceed ${maxLength} characters` });
  }

  rules.push({
    validator: createTextValidator(options)
  });

  return rules;
};

export default {
  containsDangerousContent,
  validateName,
  validateTextInput,
  sanitizeForDisplay,
  stripHtmlTags,
  createNameValidator,
  createTextValidator,
  getNameValidationRules,
  getTextValidationRules,
};
